import { isEmpty } from './helpers';
import { GroupData } from './types';

export const findMatchingGroup = (
  row: Record<string, unknown>,
  mainColumns: Array<{ key: string }>,
  groupMap: Map<string, GroupData>,
  previousRowKey: string | null,
): { key: string; firstRow: Record<string, unknown> } | null => {
  const nonEmptyColumns = new Map<string, unknown>();

  mainColumns.forEach((col) => {
    const value = row[col.key];
    if (!isEmpty(value)) {
      nonEmptyColumns.set(col.key, value);
    }
  });

  if (nonEmptyColumns.size === 0) {
    return null;
  }

  const matchingEntries: Array<{ key: string; firstRow: Record<string, unknown> }> = [];

  Array.from(groupMap.entries()).forEach(([key, group]) => {
    const firstRow = group.rows[0];

    const matches = Array.from(nonEmptyColumns.entries()).every(([columnKey, value]) => {
      return firstRow[columnKey] === value;
    });

    if (matches) {
      matchingEntries.push({ key, firstRow });
    }
  });

  if (matchingEntries.length === 0) {
    return null;
  }

  if (previousRowKey) {
    const previousMatch = matchingEntries.find((entry) => entry.key === previousRowKey);
    if (previousMatch) {
      return previousMatch;
    }
  }

  return matchingEntries[matchingEntries.length - 1];
};

export const createRecordKey = (
  row: Record<string, unknown>,
  mainColumns: Array<{ key: string }>,
  inheritFrom?: Record<string, unknown>,
): string => {
  const keyParts = mainColumns.map((col) => {
    const value = row[col.key];
    const finalValue = isEmpty(value) && inheritFrom ? inheritFrom[col.key] : value;

    if (isEmpty(finalValue)) {
      return '__EMPTY__';
    }
    return JSON.stringify(finalValue);
  });
  return keyParts.join('|||');
};

export const groupRowsByMainRecord = (
  rows: Array<Record<string, unknown>>,
  mainColumns: Array<{ key: string }>,
): Array<Array<Record<string, unknown>>> => {
  if (rows.length === 0) {
    return [];
  }

  const groupMap = new Map<string, GroupData>();
  const insertionCounter = { value: 0 };
  let previousGroupKey: string | null = null;

  rows.forEach((row) => {
    const allMainColumnsEmpty = mainColumns.every((col) => isEmpty(row[col.key]));

    if (allMainColumnsEmpty && groupMap.size > 0) {
      handleAllEmptyRow(row, groupMap);
    } else {
      const matchingGroup = findMatchingGroup(row, mainColumns, groupMap, previousGroupKey);

      if (matchingGroup) {
        previousGroupKey = handlePartialMatchRow(row, mainColumns, matchingGroup, groupMap);
      } else {
        previousGroupKey = handleNewGroupRow(row, mainColumns, groupMap, insertionCounter);
      }
    }
  });

  return Array.from(groupMap.values())
    .sort((a, b) => a.insertionIndex - b.insertionIndex)
    .map((group) => group.rows);
};

const handleAllEmptyRow = (row: Record<string, unknown>, groupMap: Map<string, GroupData>): void => {
  const groups = Array.from(groupMap.values());
  const lastGroup = groups.sort((a, b) => b.insertionIndex - a.insertionIndex)[0];
  lastGroup.rows.push(row);
};

const handlePartialMatchRow = (
  row: Record<string, unknown>,
  mainColumns: Array<{ key: string }>,
  matchingGroup: { key: string; firstRow: Record<string, unknown> },
  groupMap: Map<string, GroupData>,
): string => {
  const recordKey = createRecordKey(row, mainColumns, matchingGroup.firstRow);
  groupMap.get(recordKey)!.rows.push(row);
  return recordKey;
};

const handleNewGroupRow = (
  row: Record<string, unknown>,
  mainColumns: Array<{ key: string }>,
  groupMap: Map<string, GroupData>,
  insertionCounter: { value: number },
): string => {
  const recordKey = createRecordKey(row, mainColumns);

  if (groupMap.has(recordKey)) {
    groupMap.get(recordKey)!.rows.push(row);
  } else {
    groupMap.set(recordKey, {
      rows: [row],
      insertionIndex: insertionCounter.value++,
    });
  }
  return recordKey;
};
